import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, finalize, takeUntil } from 'rxjs';

import { PageLoaderService } from '../core/page-loader.service';
import { BirthDateInputComponent } from '../shared/birth-date-input/birth-date-input.component';
import { ParticipantAuthService } from './participant-auth.service';
import {
  ParticipantAuthenticationRequest,
  ParticipantAuthenticationResponse,
  ParticipantElectionContext,
} from './participant-auth.models';

@Component({
  selector: 'app-participant-login',
  imports: [CommonModule, ReactiveFormsModule, BirthDateInputComponent],
  templateUrl: './participant-login.component.html',
  styleUrl: './participant-login.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipantLoginComponent implements OnInit, OnDestroy {
  submetido = false;
  erroStatus = 0;
  error = '';
  infoMessage = '';
  isLoadingContext = true;
  isSubmitting = false;
  requiresPassword = false;
  isRedirecting = false;

  electionContext: ParticipantElectionContext | null = null;

  @ViewChild('password', { static: false })
  passwordInput?: ElementRef<HTMLInputElement>;

  readonly modelo: FormGroup;

  private readonly destroy$ = new Subject<void>();
  private companyPrefix = '';
  private electionId = '';

  constructor(
    private readonly fb: FormBuilder,
    private readonly title: Title,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly authService: ParticipantAuthService,
    private readonly pageLoaderService: PageLoaderService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.modelo = this.fb.group({
      cpf: ['', [Validators.required, this.validateCpf]],
      birthDate: ['', [Validators.required, this.validateBirthDate]],
      password: [''],
      lembrarMe: [true],
    });

    this.title.setTitle('Acesso SMOCipa');
  }

  get submitButtonText(): string {
    return this.requiresPassword ? 'Acessar' : 'Continuar';
  }

  ngOnInit(): void {
    this.companyPrefix = this.route.snapshot.paramMap.get('companyPrefix') ?? '';
    this.electionId = this.route.snapshot.paramMap.get('electionId') ?? '';

    console.log('Company Prefix:', this.companyPrefix);
    console.log('Election ID:', this.electionId);

    if (!this.companyPrefix || !this.electionId) {
      void this.router.navigateByUrl('/eleicao-nao-encontrada');
      return;
    }

    this.loadElectionContext();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  autenticar(args: Event): void {
    args.preventDefault();

    if (!this.electionContext || this.isSubmitting || this.isLoadingContext) {
      return;
    }

    this.submetido = true;
    this.error = '';
    this.infoMessage = '';

    this.applyPasswordRequirement();
    this.modelo.updateValueAndValidity({ emitEvent: false });

    if (!this.modelo.valid) {
      this.cdr.markForCheck();
      return;
    }

    const request = this.buildRequest();
    this.isSubmitting = true;

    const authentication$ = this.requiresPassword
      ? this.authService.authenticateWithPassword(this.companyPrefix, this.electionId, request)
      : this.authService.identifyParticipant(this.companyPrefix, this.electionId, request);

    authentication$
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => this.handleAuthenticationResponse(response),
        error: (err: unknown) => {
          this.error = err instanceof Error
            ? err.message
            : 'Ocorreu um erro ao realizar a autenticação. Tente novamente.';
          this.cdr.markForCheck();
        },
      });
  }

  recuperarSenha(): void {
    this.infoMessage = 'O fluxo de recuperação de senha será conectado ao backend nas próximas etapas.';
    this.cdr.markForCheck();
  }

  private loadElectionContext(): void {
    this.isLoadingContext = true;

    this.authService.getElectionContext(this.companyPrefix, this.electionId)
      .pipe(
        finalize(() => {
          this.isLoadingContext = false;
          this.pageLoaderService.hide();
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (context) => {
          if (!context) {
            void this.router.navigateByUrl('/eleicao-nao-encontrada');
            return;
          }

          this.electionContext = context;
          this.title.setTitle(`Acesso ${context.establishmentName}`);
          this.cdr.markForCheck();
          this.initializeBerserkFormControls();
        },
        error: (error: unknown) => {
          this.handleElectionContextError(error);
        },
      });
  }

  private handleElectionContextError(error: unknown): void {
    console.error('Error loading election context:', error);
    
    if (!(error instanceof HttpErrorResponse)) {
      void this.router.navigateByUrl('/erro-generico');
      return;
    }

    if (error.status === 404) {
      void this.router.navigateByUrl('/eleicao-nao-encontrada');
      return;
    }

    if (error.status === 0 || error.status === 504) {
      void this.router.navigateByUrl('/erro-comunicacao');
      return;
    }

    void this.router.navigateByUrl('/erro-generico');
  }

  private applyPasswordRequirement(): void {
    const passwordControl = this.modelo.get('password');

    if (!passwordControl) {
      return;
    }

    if (this.requiresPassword) {
      passwordControl.setValidators([Validators.required]);
    } else {
      passwordControl.clearValidators();
    }

    passwordControl.updateValueAndValidity({ emitEvent: false });
  }

  private buildRequest(): ParticipantAuthenticationRequest {
    const cpf = this.extractDigits(this.modelo.get('cpf')?.value ?? '');
    const birthDate = this.normalizeBirthDate(this.modelo.get('birthDate')?.value ?? '');

    return {
      cpf,
      birthDate,
      companySuffix: this.companyPrefix,
      electionGuid: this.electionId,
      password: this.requiresPassword ? (this.modelo.get('password')?.value ?? '') : null,
    };
  }

  private handleAuthenticationResponse(response: ParticipantAuthenticationResponse): void {
    if (response.requiresPassword && !response.authenticated) {
      const passwordControl = this.modelo.get('password');

      this.requiresPassword = true;
      this.submetido = false;
      this.infoMessage = response.message || `Participante identificado: ${response.participantName}. Informe sua senha para continuar.`;
      this.applyPasswordRequirement();
      passwordControl?.setValue('');
      passwordControl?.markAsPristine();
      passwordControl?.markAsUntouched();
      this.cdr.markForCheck();
      this.initializeBerserkFormControls(() => this.passwordInput?.nativeElement.focus());
      return;
    }

    if (!response.authenticated || !response.code) {
      this.error = response.message || 'Não foi possível concluir a autenticação.';
      this.cdr.markForCheck();
      return;
    }

    const storageType = this.getSelectedStorageType();
    const redirectUrl = this.authService.buildVotingRedirectUrl(response.code, storageType);
    this.isRedirecting = true;
    this.cdr.markForCheck();
    window.location.assign(redirectUrl);
  }

  private getSelectedStorageType(): 1 | 2 {
    const rememberMe = !!this.modelo.get('lembrarMe')?.value;
    return rememberMe ? 1 : 2;
  }

  private initializeBerserkFormControls(afterAttach?: () => void): void {
    setTimeout(() => {
      requestAnimationFrame(() => {
        const globalWindow = window as Window & {
          Berserk?: {
            settings?: unknown;
            behaviors?: {
              form_controls?: {
                attach?: (context: unknown, settings: unknown) => void;
              };
            };
          };
          jQuery?: (selector: unknown) => unknown;
        };

        const attach = globalWindow.Berserk?.behaviors?.form_controls?.attach;
        const jquery = globalWindow.jQuery;

        if (typeof attach === 'function' && typeof jquery === 'function') {
          attach(jquery('body'), globalWindow.Berserk?.settings ?? {});
        }

        afterAttach?.();
      });
    }, 0);
  }

  private readonly validateCpf = (control: AbstractControl): ValidationErrors | null => {
    const digits = this.extractDigits(control.value ?? '');

    if (!digits) {
      return null;
    }

    return digits.length === 11 ? null : { cpfLength: true };
  };

  private readonly validateBirthDate = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    return this.parseBirthDate(control.value) ? null : { invalidBirthDate: true };
  };

  private extractDigits(value: string): string {
    return String(value ?? '').replace(/\D/g, '');
  }

  formatRegistrationNumber(number: string, type: string): string {
    const digits = number.replace(/\D/g, '');

    if (type === 'cnpj' && digits.length === 14) {
      return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
    }

    if (type === 'caepf' && digits.length >= 14) {
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}/${digits.slice(9, 12)}-${digits.slice(12, 14)}`;
    }

    return number;
  }

  private normalizeBirthDate(value: string): string {
    const parsedDate = this.parseBirthDate(value);

    if (!parsedDate) {
      return String(value ?? '');
    }

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseBirthDate(value: string): Date | null {
    const rawValue = String(value ?? '').trim();

    if (!rawValue) {
      return null;
    }

    const brMatch = rawValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (brMatch) {
      // Máscara automática: DD/MM/YYYY
      const [, dayString, monthString, yearString] = brMatch;
      return this.createValidatedDate(Number(yearString), Number(monthString), Number(dayString));
    }

    const isoMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const [, yearString, monthString, dayString] = isoMatch;
      return this.createValidatedDate(Number(yearString), Number(monthString), Number(dayString));
    }

    return null;
  }

  private createValidatedDate(year: number, month: number, day: number): Date | null {
    if (!year || !month || !day) {
      return null;
    }

    const date = new Date(year, month - 1, day);

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }
}
